import * as React from 'react';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import RetourIcon from '@mui/icons-material/ArrowBack';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { chargerClubAdmin, chargerClubsAdmin, type ClubAdmin, type MembreClub } from '../Auth/moi.ts';

export const CHEMIN_CLUBS_ADMIN = '/admin/clubs';

const nomMembre = (m: MembreClub) => m.nom || m.email;

function formaterDate(iso?: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { dateStyle: 'medium' });
}

const pluriel = (n: number, mot: string) => `${n} ${mot}${n > 1 ? 's' : ''}`;

const ChipPersonnel = ({ club }: { club: ClubAdmin }) =>
  club.personnel ? <Chip size="small" variant="outlined" label="Personnel" sx={{ ml: 1 }} /> : null;

/**
 * Administration > Clubs : tous les clubs de l'application, et le detail
 * d'un club (/admin/clubs/:id). Reserve aux administrateurs de
 * l'application (ReserveAdmin dans App.tsx ; AdminClubController cote serveur).
 */
export default function ClubsAdmin() {
  const { id } = useParams();
  return id ? <DetailClub id={id} /> : <ListeClubs />;
}

function ListeClubs() {
  const navigate = useNavigate();
  const [clubs, setClubs] = React.useState<ClubAdmin[] | null>(null);
  const [erreur, setErreur] = React.useState<string | null>(null);
  const [recherche, setRecherche] = React.useState('');

  React.useEffect(() => {
    chargerClubsAdmin()
      .then(setClubs)
      .catch((e) => setErreur('Chargement impossible : ' + (e as Error).message));
  }, []);

  const filtre = recherche.trim().toLowerCase();
  const affiches = (clubs ?? []).filter(
    (c) =>
      !filtre ||
      c.nom.toLowerCase().includes(filtre) ||
      c.administrateurs.some((a) => nomMembre(a).toLowerCase().includes(filtre) || a.email.toLowerCase().includes(filtre)),
  );

  return (
    <Box component="main" sx={{ p: { xs: 2, md: 12 }, pt: { xs: 10, md: 10 } }}>
      <Typography sx={{ mb: 2 }} color="text.secondary">
        Tous les clubs de l'application. Un club « personnel » est créé automatiquement pour chaque coach indépendant.
      </Typography>

      {erreur && <Alert severity="error" sx={{ mb: 2 }}>{erreur}</Alert>}
      {!clubs && !erreur && <CircularProgress />}

      {clubs && (
        <>
          <TextField
            size="small"
            label="Rechercher (club ou administrateur)"
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
            sx={{ mb: 2, width: { xs: '100%', sm: 360 } }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {pluriel(clubs.length, 'club')}, dont {clubs.filter((c) => c.personnel).length} personnel
            {clubs.filter((c) => c.personnel).length > 1 ? 's' : ''}
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Club</TableCell>
                  <TableCell>Administrateurs</TableCell>
                  <TableCell>Membres</TableCell>
                  <TableCell>Créé le</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {affiches.map((c) => (
                  <TableRow
                    key={c.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`${CHEMIN_CLUBS_ADMIN}/${encodeURIComponent(c.id)}`)}
                  >
                    <TableCell>
                      <Link to={`${CHEMIN_CLUBS_ADMIN}/${encodeURIComponent(c.id)}`} onClick={(e) => e.stopPropagation()}>
                        {c.nom}
                      </Link>
                      <ChipPersonnel club={c} />
                    </TableCell>
                    <TableCell>
                      {c.administrateurs.length === 0 ? '—' : c.administrateurs.map(nomMembre).join(', ')}
                    </TableCell>
                    <TableCell>
                      {pluriel(c.nbCoachs, 'coach')}, {pluriel(c.nbAthletes, 'athlète')}
                    </TableCell>
                    <TableCell>{formaterDate(c.dateCreation)}</TableCell>
                  </TableRow>
                ))}
                {affiches.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="text.secondary">Aucun club.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}

function DetailClub({ id }: { id: string }) {
  // undefined : chargement ; null : club inconnu.
  const [club, setClub] = React.useState<ClubAdmin | null | undefined>(undefined);
  const [erreur, setErreur] = React.useState<string | null>(null);

  React.useEffect(() => {
    setClub(undefined);
    setErreur(null);
    chargerClubAdmin(id)
      .then(setClub)
      .catch((e) => setErreur('Chargement impossible : ' + (e as Error).message));
  }, [id]);

  return (
    <Box component="main" sx={{ p: { xs: 2, md: 12 }, pt: { xs: 10, md: 10 }, maxWidth: 900 }}>
      <Button component={Link} to={CHEMIN_CLUBS_ADMIN} startIcon={<RetourIcon />} sx={{ mb: 2 }}>
        Tous les clubs
      </Button>

      {erreur && <Alert severity="error" sx={{ mb: 2 }}>{erreur}</Alert>}
      {club === undefined && !erreur && <CircularProgress />}
      {club === null && <Alert severity="warning">Ce club n'existe pas (ou plus).</Alert>}

      {club && (
        <>
          <Typography variant="h5" component="h1" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
            {club.nom}
            <ChipPersonnel club={club} />
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Créé le {formaterDate(club.dateCreation)} · {pluriel(club.nbCoachs, 'coach')} ·{' '}
            {pluriel(club.nbAthletes, 'athlète')}
            {club.nbInvitations > 0 && ` · ${pluriel(club.nbInvitations, 'invitation')} en attente`}
          </Typography>

          <Typography variant="h6" component="h2">Administrateurs</Typography>
          <Paper variant="outlined" sx={{ mt: 1 }}>
            <List dense>
              {club.administrateurs.map((a) => (
                <ListItem key={a.id}>
                  <ListItemAvatar>
                    <Avatar alt={nomMembre(a)} src={a.photo ?? undefined} imgProps={{ referrerPolicy: 'no-referrer' }}>
                      {nomMembre(a).charAt(0).toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={nomMembre(a)}
                    secondary={[a.nom ? a.email : null, a.coach ? 'Coach' : 'Athlète', a.bibliothecaire ? 'bibliothécaire' : null]
                      .filter(Boolean)
                      .join(' · ')}
                  />
                </ListItem>
              ))}
              {club.administrateurs.length === 0 && (
                <ListItem>
                  <ListItemText secondary="Aucun administrateur." />
                </ListItem>
              )}
            </List>
          </Paper>
        </>
      )}
    </Box>
  );
}
